'use client';

import { Box, Typography } from '@/primitives';
import type { TypographyProps } from '@mui/material/Typography';
import EbvTraitInfoIcon from './EbvTraitInfoIcon';
import {
  EBV_TRAIT_INFO_ICON_SIZE,
  formatEbvLabelPlain,
  getEbvConfigTraitKey,
  type EbvTraitHeaderContext,
} from './ebvTraitExplanations';
import type { EbvConfigItem } from '@/hooks/publicSearch/ebv/ebvColumns';

type EbvTraitHeaderLabelProps = {
  label: string;
  traitKey?: string;
  traitHeaderContext?: EbvTraitHeaderContext;
  typographyProps?: TypographyProps;
  iconSize?: number;
  hideIconOnMobile?: boolean;
  align?: 'left' | 'center';
  /** Grid headers stack the icon below the label; charts keep it inline. */
  iconLayout?: 'stacked' | 'inline';
};

export function EbvTraitHeaderLabel({
  label,
  traitKey,
  traitHeaderContext,
  typographyProps,
  iconSize = EBV_TRAIT_INFO_ICON_SIZE,
  hideIconOnMobile = false,
  align = 'center',
  iconLayout = 'stacked',
}: EbvTraitHeaderLabelProps) {
  const resolvedTraitKey = traitKey?.trim();
  const entry =
    resolvedTraitKey &&
    traitHeaderContext?.traitExplanationsByKey.get(resolvedTraitKey);

  const plainLabel = formatEbvLabelPlain(label);
  const showIcon = Boolean(entry && traitHeaderContext?.onTraitInfoClick);
  const isInline = iconLayout === 'inline';
  const alignItems = isInline || align === 'center' ? 'center' : 'flex-start';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isInline ? 'row' : 'column',
        alignItems,
        justifyContent: isInline ? undefined : 'flex-end',
        width: isInline ? undefined : '100%',
        maxWidth: '100%',
        lineHeight: 1.2,
        gap: showIcon ? 0.25 : 0,
        minWidth: isInline ? 0 : undefined,
      }}
    >
      <Typography
        component="span"
        variant="bodysmall"
        {...typographyProps}
        sx={[
          {
            color: 'text.primary',
            lineHeight: 1.2,
            textAlign: align,
            ...(isInline
              ? { minWidth: 0, flex: '1 1 auto' }
              : { width: '100%' }),
          },
          ...(Array.isArray(typographyProps?.sx)
            ? typographyProps.sx
            : [typographyProps?.sx]),
        ]}
      >
        {plainLabel}
      </Typography>
      {showIcon && entry && traitHeaderContext?.onTraitInfoClick && (
        <EbvTraitInfoIcon
          label={plainLabel}
          entry={entry}
          onClick={traitHeaderContext.onTraitInfoClick}
          iconSize={iconSize}
          sx={{
            ...(hideIconOnMobile
              ? { display: { xs: 'none', md: 'inline-flex' } }
              : undefined),
          }}
        />
      )}
    </Box>
  );
}

type EbvTraitHeaderLabelFromConfigProps = Omit<
  EbvTraitHeaderLabelProps,
  'traitKey'
> & {
  config: Pick<EbvConfigItem, 'traitKey' | 'TraitKey'>;
};

export function EbvTraitHeaderLabelFromConfig({
  config,
  ...props
}: EbvTraitHeaderLabelFromConfigProps) {
  return (
    <EbvTraitHeaderLabel {...props} traitKey={getEbvConfigTraitKey(config)} />
  );
}
